import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { api } from '../api';

/**
 * Lead fields the importer understands, each with a set of accepted column
 * headers (Hebrew + English aliases). `phone` is the required key; every other
 * column is optional. Header matching is accent/space/punctuation-insensitive.
 */
const FIELD_ALIASES: Array<{ field: string; label: string; aliases: string[] }> = [
  { field: 'phone', label: 'טלפון', aliases: ['טלפון', 'מספר טלפון', 'נייד', 'פלאפון', 'phone', 'mobile', 'tel'] },
  { field: 'customer_name', label: 'שם לקוח', aliases: ['שם', 'שם לקוח', 'שם מלא', 'לקוח', 'name', 'customer', 'customername', 'fullname'] },
  { field: 'national_id', label: 'ת"ז', aliases: ['תז', 'תעודת זהות', 'מספר זהות', 'nationalid', 'id', 'idnumber'] },
  { field: 'car_number', label: 'מספר רכב', aliases: ['מספר רכב', 'רכב', 'מספר רישוי', 'לוחית', 'carnumber', 'plate', 'platenumber'] },
  { field: 'car_manufacturer', label: 'יצרן', aliases: ['יצרן', 'יצרן רכב', 'manufacturer', 'make'] },
  { field: 'car_model', label: 'דגם', aliases: ['דגם', 'model'] },
  { field: 'car_year', label: 'שנת ייצור', aliases: ['שנה', 'שנת ייצור', 'year', 'caryear'] },
  { field: 'assigned_to', label: 'נציג מטפל', aliases: ['נציג', 'נציג מטפל', 'מטפל', 'agent', 'assignedto', 'owner'] },
  { field: 'coverage_type', label: 'סוג כיסוי', aliases: ['כיסוי', 'סוג כיסוי', 'coverage', 'coveragetype'] },
  { field: 'main_driver_city', label: 'עיר', aliases: ['עיר', 'city'] },
];

/** Collapse a header to a comparable token: strip spaces/quotes/dots, lowercase. */
function norm(s: string): string {
  return String(s)
    .toLowerCase()
    .replace(/["'`.\-_/\\]/g, '')
    .replace(/[\u05f3\u05f4]/g, '')
    .replace(/\s+/g, '')
    .trim();
}

const FIELD_BY_NORM = new Map<string, string>();
for (const { field, aliases } of FIELD_ALIASES) {
  for (const a of aliases) FIELD_BY_NORM.set(norm(a), field);
}

/** Map a coverage cell (Hebrew/English/code) to the canonical coverage_type. */
function normalizeCoverage(raw: string): string | undefined {
  const v = norm(raw);
  if (!v) return undefined;
  if (v.includes('מקיף') || v.includes('comprehensive')) return 'comprehensive';
  if (v.includes('צדג') || v.includes('צג') || v.includes('thirdparty') || v.includes('3')) return 'third_party';
  if (v === 'comprehensive' || v === 'thirdparty' || v === 'third_party') return v === 'thirdparty' ? 'third_party' : v;
  return undefined;
}

interface ParsedResult {
  rows: Array<Record<string, unknown>>;
  matched: Array<{ header: string; field: string }>;
  ignored: string[];
  invalidPhone: number;
  fileName: string;
}

type Summary = {
  created: number;
  updated: number;
  skipped: number;
  failed: number;
};

/**
 * Excel/CSV lead importer. Reads the first sheet, auto-maps columns to lead
 * fields by header name, keys rows by phone, previews the result, then bulk
 * upserts via POST /leads/import (mode: update = merge, skip = keep existing).
 */
export function ImportLeadsModal({
  tenantId,
  onClose,
  onImported,
}: {
  tenantId?: string;
  onClose: () => void;
  onImported: () => void;
}) {
  const [parsed, setParsed] = useState<ParsedResult | null>(null);
  const [mode, setMode] = useState<'update' | 'skip'>('update');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function downloadTemplate() {
    const headers = FIELD_ALIASES.map((f) => f.label);
    const example = ['0521234567', 'ישראל ישראלי', '312345678', '12-345-67', 'טויוטה', 'קורולה', 2020, 'דנה', 'מקיף', 'תל אביב'];
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'לידים');
    XLSX.writeFile(wb, 'leads-template.xlsx');
  }

  async function handleFile(file: File) {
    setError(null);
    setSummary(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) {
        setError('הקובץ ריק או שאין בו גיליון.');
        return;
      }
      const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '', raw: false });
      if (json.length === 0) {
        setError('לא נמצאו שורות נתונים בקובץ.');
        return;
      }

      const headerKeys = Object.keys(json[0]);
      const headerToField = new Map<string, string>();
      const matched: Array<{ header: string; field: string }> = [];
      const ignored: string[] = [];
      for (const h of headerKeys) {
        const field = FIELD_BY_NORM.get(norm(h));
        if (field && !Array.from(headerToField.values()).includes(field)) {
          headerToField.set(h, field);
          matched.push({ header: h, field });
        } else {
          ignored.push(h);
        }
      }

      if (!Array.from(headerToField.values()).includes('phone')) {
        setError('לא זוהתה עמודת טלפון. ודא שקיימת כותרת "טלפון" (מפתח הליד).');
        setParsed(null);
        return;
      }

      let invalidPhone = 0;
      const rows: Array<Record<string, unknown>> = [];
      for (const raw of json) {
        const row: Record<string, unknown> = {};
        for (const [header, field] of headerToField) {
          let value = raw[header];
          if (value === undefined || value === null || String(value).trim() === '') continue;
          value = String(value).trim();
          if (field === 'car_year') {
            const n = Number(value);
            if (!Number.isNaN(n)) row.car_year = n;
            continue;
          }
          if (field === 'coverage_type') {
            const c = normalizeCoverage(String(value));
            if (c) row.coverage_type = c;
            continue;
          }
          row[field] = value;
        }
        const digits = String(row.phone ?? '').replace(/\D/g, '');
        if (digits.length < 9) {
          invalidPhone += 1;
          continue;
        }
        rows.push(row);
      }

      setParsed({ rows, matched, ignored, invalidPhone, fileName: file.name });
    } catch (e) {
      setError(`קריאת הקובץ נכשלה: ${e}`);
    }
  }

  async function runImport() {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.importLeads(parsed.rows, mode, tenantId);
      setSummary({ created: res.created, updated: res.updated, skipped: res.skipped, failed: res.failed });
    } catch (e) {
      setError(`הייבוא נכשל: ${e}`);
    } finally {
      setBusy(false);
    }
  }

  const labelOf = (field: string) => FIELD_ALIASES.find((f) => f.field === field)?.label ?? field;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>ייבוא לידים מאקסל</h2>
          <button className="modal-close" onClick={onClose} aria-label="סגור">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="lead-note warn">{error}</div>}

          {summary ? (
            <div className="import-summary">
              <h3>הייבוא הושלם</h3>
              <div className="import-stats">
                <div className="import-stat"><strong>{summary.created}</strong><span>נוצרו</span></div>
                <div className="import-stat"><strong>{summary.updated}</strong><span>עודכנו</span></div>
                <div className="import-stat"><strong>{summary.skipped}</strong><span>דולגו</span></div>
                <div className="import-stat"><strong>{summary.failed}</strong><span>נכשלו</span></div>
              </div>
            </div>
          ) : (
            <>
              <p className="import-intro">
                בחר קובץ Excel (‎.xlsx/.xls‎) או CSV. שדה המפתח הוא <strong>מספר הטלפון</strong> — שורה קיימת תזוהה לפיו.
                הכותרות מזוהות אוטומטית (עברית/אנגלית). ניתן להוריד תבנית מוכנה.
              </p>

              <div className="import-actions-row">
                <button className="btn outline" onClick={downloadTemplate}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                  הורדת תבנית
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                  }}
                />
                <button className="btn" onClick={() => fileRef.current?.click()}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 14.9V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2.1M12 3v12M7 8l5-5 5 5" /></svg>
                  בחירת קובץ
                </button>
              </div>

              {parsed && (
                <div className="import-preview">
                  <div className="import-preview-head">
                    <span className="chip">{parsed.fileName}</span>
                    <span><strong>{parsed.rows.length.toLocaleString('he-IL')}</strong> שורות תקינות</span>
                    {parsed.invalidPhone > 0 && <span className="warn-text">{parsed.invalidPhone} ללא טלפון תקין (יידלגו)</span>}
                  </div>

                  <div className="import-mapping">
                    <span className="flabel">עמודות שזוהו:</span>
                    {parsed.matched.map((m) => (
                      <span key={m.header} className="chip mapped">{m.header} → {labelOf(m.field)}</span>
                    ))}
                    {parsed.ignored.length > 0 && (
                      <span className="import-ignored">התעלמות מ: {parsed.ignored.join(', ')}</span>
                    )}
                  </div>

                  {parsed.rows.length > 0 && (
                    <div className="import-sample">
                      <table>
                        <thead>
                          <tr>
                            {parsed.matched.map((m) => (
                              <th key={m.field}>{labelOf(m.field)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {parsed.rows.slice(0, 5).map((r, i) => (
                            <tr key={i}>
                              {parsed.matched.map((m) => (
                                <td key={m.field}>{String(r[m.field] ?? '')}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {parsed.rows.length > 5 && <div className="cell-sub">…ועוד {(parsed.rows.length - 5).toLocaleString('he-IL')} שורות</div>}
                    </div>
                  )}

                  <div className="import-mode">
                    <span className="flabel">בהתנגשות (טלפון קיים):</span>
                    <label><input type="radio" name="mode" checked={mode === 'update'} onChange={() => setMode('update')} /> עדכן שדות</label>
                    <label><input type="radio" name="mode" checked={mode === 'skip'} onChange={() => setMode('skip')} /> דלג (השאר קיים)</label>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {summary ? (
            <button className="btn" onClick={() => { onImported(); onClose(); }}>סיום</button>
          ) : (
            <>
              <button className="btn outline" onClick={onClose} disabled={busy}>ביטול</button>
              <button className="btn" onClick={runImport} disabled={busy || !parsed || parsed.rows.length === 0}>
                {busy ? 'מייבא…' : parsed ? `ייבא ${parsed.rows.length.toLocaleString('he-IL')} לידים` : 'ייבא'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
